import React, { useState } from 'react';

import { formatCurrency } from '@atc/string-fns';

import { useDevice } from '@atc/react-device';
import { sendClick } from 'reaxl-analytics';
import { inventoryClick } from 'reaxl-analytics-handlers';
import { useFeatures } from '@atc/features';

import { Glyphicon, Image, Text, Link } from 'reaxl';
import { Link as LinkButton } from '@iris/react-components';

import { InventoryImagePlaceholder, OwnerPhoneNumber, OwnerTextCTA } from 'reaxl-molecules';

import DealCardAlert from './DealCardAlert';

const pipe = <span className="padding-horizontal-2">|</span>;

function DealCardVehicleOverview({ dealData = {}, indexClicked, onEmailDealerClick, onPhoneClick, onTextClick }) {
    const device = useDevice();
    const isXs = device?.is?.xs || false;

    const [imageIsBroken, setImageIsBroken] = useState(false);

    const {
        active_deals_my_wallet: [, { contact_email: enableEmail, contact_phone: enablePhone, contact_sms: enableSMS }],
    } = useFeatures(['active_deals_my_wallet']);

    const { vehicle = {}, dealer, deal = {} } = dealData;
    const { phone = {}, textNumber = '', name: dealerName } = dealer;
    const { imageUrl, sellingPrice, title, vdpUrl } = vehicle;
    const { dealType = '', payment } = deal;

    const showPhone = enablePhone && isXs && !!Object.keys(phone).length;
    const showSMS = enableSMS && isXs && !!textNumber;

    const onVehicleClick = (event) =>
        sendClick(inventoryClick, event, {
            par: 'dealdetails',
            vehicle,
            indexClicked,
        });

    const handleImageError = () => setImageIsBroken(true);

    const analyticsData = {
        inventoryId: vehicle?.id,
        ownerId: dealer?.id,
        placement: 'myWallet', // Tentative to change in tagging story.
    };

    const vehicleTitle = vdpUrl ? (
        <Link
            href={vdpUrl}
            className="text-bold margin-bottom-1"
            undecorated
        >
            {title}
        </Link>
    ) : (
        <Text
            weight="bold"
            className="margin-bottom-1"
        >
            {title}
        </Text>
    );

    return (
        <>
            <DealCardAlert {...dealData} />
            <div
                className="margin-bottom-2 display-flex"
                onClick={onVehicleClick}
                role="button"
                tabIndex="0"
            >
                <div style={{ maxWidth: '33%' }}>
                    {imageUrl && !imageIsBroken ? (
                        <Image
                            src={imageUrl.replace('https:https:', 'https:')}
                            style={{ width: '100%' }}
                            onError={handleImageError}
                        />
                    ) : (
                        <InventoryImagePlaceholder
                            className="rounded padding-2"
                            height={75}
                        />
                    )}
                </div>
                <div className="margin-left-3">
                    {vehicleTitle}

                    <div className="display-flex flex-wrap">
                        <Text
                            weight="bold"
                            className="display-block"
                        >
                            {formatCurrency(sellingPrice)}
                        </Text>
                        {pipe}
                        <Text transformCase="capitalize">{dealType}</Text>
                        {dealType?.toLowerCase() !== 'cash' && (
                            <>
                                {pipe}
                                <Text>{`Est. ${formatCurrency(Math.round(payment))}/mo.`}</Text>
                            </>
                        )}
                    </div>
                    {dealer && (
                        <>
                            {dealerName && <Text>{dealerName}</Text>}
                            {(enableEmail || showSMS || showPhone) && (
                                <div className="display-flex">
                                    {enableEmail && (
                                        <LinkButton
                                            variant="standalone"
                                            className="iris:font-(--comp-type-weight-link-bold)! iris:text-(--sem-color-content-neutral-primary) iris:hover:text-(--sem-color-content-neutral-primary) iris:mr-6"
                                            onClick={(event) => onEmailDealerClick(event, 'wallet')}
                                        >
                                            <Glyphicon
                                                glyph="envelope"
                                                className="margin-right-2 text-accent"
                                            />
                                            Email
                                        </LinkButton>
                                    )}

                                    {showSMS && (
                                        <OwnerTextCTA
                                            analyticsData={analyticsData}
                                            buttonBsStyle="link"
                                            iconClassName="text-accent"
                                            className="link-unstyled text-bold margin-right-5 padding-0"
                                            label="Text"
                                            phoneNumber={textNumber}
                                            textBody={`I'd like to follow up on my deal for the ${title}`}
                                            uiContext="button"
                                            key="text-cta-owner-text"
                                            textClickHandler={onTextClick}
                                        />
                                    )}
                                    {showPhone && (
                                        <OwnerPhoneNumber
                                            analyticsData={analyticsData}
                                            buttonStyle="link"
                                            iconClassName="text-accent"
                                            className="link-unstyled text-bold padding-0"
                                            phone={phone}
                                            uiContext="button"
                                            buttonLabel="Call"
                                            key="call-cta-owner-phone-number"
                                            phoneClickHandler={onPhoneClick}
                                        />
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </>
    );
}

export default DealCardVehicleOverview;
